import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { ReservationsChart } from "./Components/ReservationsChart";
import { fetchHotel } from "../../../Redux/slice/features/dropdownHotelSlice";
import { REACT_APP_API_URL } from "../../../env";
import { DashboardStatsCards } from "./Components/DashboardStatsCards";
import { AdminRevenueKpiSection } from "./Components/AdminRevenueKpiSection";
import { AdminHotelDailyRevenueSection } from "./Components/AdminHotelDailyRevenueSection";
import { AdminTopRevenueHotelsSection } from "./Components/AdminTopRevenueHotelsSection";
import { AdminOccupancySection } from "./Components/AdminOccupancySection";
import { ActivityRevenueSection } from "./Components/ActivityRevenueSection";
import { BookingTrendsSection } from "./Components/BookingTrendsSection";
import { CancellationTrendSection } from "./Components/CancellationTrendSection";
import {
  HEATMAP_MONTHS,
  MONTH_ORDER,
  mapMonthlyData,
  parseOccupancyData,
  parseRevenueApiData,
  REVENUE_FILTER_OPTIONS,
  getRevenueValue,
  toMonthShort,
  toNumericValue,
} from "./Components/dashboardUtils";

const Index = () => {
  const MIN_REVENUE_YEAR = 2025;
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const { user } = useSelector((state) => state.auth);
  const userType = useSelector((state) => state.auth.userType);
  const allHotel = useSelector((state) => state.hotels.hoteldata);

  const [selectedHotel, setSelectedHotel] = useState("");
  const [availableHotels, setAvailableHotels] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedChartType, setSelectedChartType] = useState("line");
  const [chartSeries, setChartSeries] = useState([]);
  const [chartOptions, setChartOptions] = useState(ReservationsChart);
  const [dashboardData, setDashboardData] = useState(null);

  const [cancellationData, setCancellationData] = useState([]);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationError, setCancellationError] = useState("");

  const [occupancySeries, setOccupancySeries] = useState([]);
  const [occupancyOptions, setOccupancyOptions] = useState({
    chart: { type: "pie" },
    labels: [],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    tooltip: {
      y: {
        formatter: (val) => `${val} Rooms`,
      },
    },
  });
  const [occupancyLoading, setOccupancyLoading] = useState(false);
  const [occupancyError, setOccupancyError] = useState("");
  const [occupancySummary, setOccupancySummary] = useState({
    totalRooms: 0,
    occupiedRooms: 0,
    availableRooms: 0,
    bookingPercentage: 0,
  });

  const [selectedRevenueType, setSelectedRevenueType] = useState("monthly");
  const [selectedRevenueYear, setSelectedRevenueYear] = useState(currentYear);
  const [selectedRevenueMonth, setSelectedRevenueMonth] = useState(new Date().getMonth() + 1);
  const [monthlySnapshot, setMonthlySnapshot] = useState({
    currentMonthRevenue: 0,
    lastMonthRevenue: 0,
  });
  const [monthlySnapshotLoading, setMonthlySnapshotLoading] = useState(false);
  const [monthlySnapshotError, setMonthlySnapshotError] = useState("");
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [revenueOptions, setRevenueOptions] = useState({
    chart: {
      type: "area",
      toolbar: { show: false },
    },
    colors: ["#0284c7"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: [],
      title: {
        text: "Timeline",
      },
      crosshairs: {
        show: true,
        position: 'back',
        stroke: {
          color: '#111827',
          width: 1,
          dashArray: 0,
        },
      },
      tooltip: { enabled: false }
    },
    yaxis: {
      title: {
        text: "Revenue (INR)",
      },
      labels: {
        formatter: (value) => {
          if (value >= 1000) return "\u20B9" + (value / 1000).toFixed(0) + "k";
          return "\u20B9" + value;
        },
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        const category = w.globals.labels[dataPointIndex] || "";
        const formattedValue = new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(value);

        const svgSparkline = `
          <svg width="40" height="15" viewBox="0 0 40 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 10C5 10 7 5 11 5C15 5 18 12 22 12C26 12 29 2 33 2C37 2 39 8 39 8" stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;

        return `
          <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); padding: 16px; min-width: 180px;">
            <div style="font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px; font-family: Inter, sans-serif;">Revenue</div>
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 24px;">
              <span style="font-size: 26px; font-weight: 700; color: #111827; line-height: 1; font-family: Inter, sans-serif;">${formattedValue}</span>
              <span style="background: #dcfce7; color: #166534; padding: 2px 6px; border-radius: 4px; font-size: 12px; font-weight: 600; border: 1px solid #bbf7d0; display: flex; align-items: center; height: 24px;">\u2191</span>
              <span style="margin-left: auto;">${svgSparkline}</span>
            </div>
            <div style="font-size: 13px; color: #6b7280; font-family: Inter, sans-serif; background: #f9fafb; margin: 0 -16px -16px -16px; padding: 12px 16px; border-top: 1px solid #e5e7eb; border-bottom-left-radius: 6px; border-bottom-right-radius: 6px;">
              ${category}
            </div>
          </div>
        `;
      },
    },
    dataLabels: { enabled: false },
    markers: { size: 0, hover: { size: 5 } },
  });
  const [revenueChartType, setRevenueChartType] = useState("area");
  const [revenueChartLoading, setRevenueChartLoading] = useState(false);
  const [revenueChartError, setRevenueChartError] = useState("");
  const [selectedHotelRevenueYear, setSelectedHotelRevenueYear] = useState(currentYear);
  const [selectedHotelRevenueMonth, setSelectedHotelRevenueMonth] = useState(
    new Date().getMonth() + 1,
  );
  const [hotelRevenueSeries, setHotelRevenueSeries] = useState([]);
  const [hotelRevenueLoading, setHotelRevenueLoading] = useState(false);
  const [hotelRevenueError, setHotelRevenueError] = useState("");
  const [hotelRevenueOptions, setHotelRevenueOptions] = useState({
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
    legend: {
      show: false,
      position: "top",
      horizontalAlign: "left",
    },
    xaxis: {
      type: "category",
      categories: [],
      title: {
        text: "Day of Month",
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.55,
        radius: 2,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: "#E9ECEF", name: "No Revenue" },
            { from: 0.01, to: 10000, color: "#dbeafe", name: "Low" },
            { from: 10000.01, to: 50000, color: "#93c5fd", name: "Moderate" },
            { from: 50000.01, to: 100000, color: "#3b82f6", name: "High" },
            { from: 100000.01, to: 999999999, color: "#1d4ed8", name: "Peak" },
          ],
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value) =>
          new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
          }).format(Number(value) || 0),
      },
    },
  });

  const [heatmapSeries, setHeatmapSeries] = useState([]);
  const [heatmapOptions, setHeatmapOptions] = useState({
    chart: {
      type: "heatmap",
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "category",
      categories: HEATMAP_MONTHS,
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 2,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: "#E9ECEF", name: "0%" },
            { from: 0.01, to: 25, color: "#1f77b4", name: "Low" },
            { from: 25.01, to: 50, color: "#fdc70c", name: "Medium" },
            { from: 50.01, to: 75, color: "#f3903f", name: "High" },
            { from: 75.01, to: 100, color: "#e93e3a", name: "Peak" },
          ],
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    legend: { show: true },
  });
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState("");
  const [selectedHeatmapMonth, setSelectedHeatmapMonth] = useState(new Date().getMonth() + 1);

  const [comparisonYears, setComparisonYears] = useState({
    current: currentYear,
    previous: previousYear,
  });

  const userHotelId = user?.user?.PropertyID;
  const hasHotelScope = userHotelId !== null && userHotelId !== undefined;
  const isAdminUser =Number(user?.user?.customerId) === 1 || user?.user?.UserName?.toString().toLowerCase() === "admin";
  const roleValues = [
    user?.user?.Role,
    user?.user?.role,
    user?.user?.roleName,
    user?.user?.user_role,
    user?.user?.designation,
    user?.user?.Designation,
    user?.user?.vchDesignation,
    ...(Array.isArray(user?.user?.roles) ? user.user.roles : []),
  ];
  const isManagerUser =
    !isAdminUser &&
    (hasHotelScope ||
      roleValues.some((role) =>
        String(role || "").toLowerCase().includes("manager"),
      ));
  const canViewDashboardStats =
    isAdminUser || (isManagerUser && hasHotelScope);
  const buildDailyRevenueSeries = (payload, year, month) => {
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    const categories = Array.from({ length: daysInMonth }, (_, idx) => String(idx + 1));
    const dayRevenueMap = new Map();

    const rows = Array.isArray(payload)
      ? payload
      : payload?.daily ||
        payload?.dailyRevenue ||
        payload?.dailyData ||
        payload?.rows ||
        payload?.list ||
        payload?.items ||
        payload?.data ||
        [];

    const revenueKeys = [
      "Revenue",
      "revenue",
      "TotalRevenue",
      "totalRevenue",
      "DailyRevenue",
      "dailyRevenue",
      "Amount",
      "amount",
      "Value",
      "value",
      "Total",
      "total",
    ];

    rows.forEach((item) => {
      if (!item || typeof item !== "object") return;

      let day = Number(item.Day ?? item.day);
      if (!Number.isFinite(day) || day <= 0) {
        const rawDate =
          item.Date ||
          item.date ||
          item.RevenueDate ||
          item.revenueDate ||
          item.BookingDate ||
          item.bookingDate;
        const parsedDate = rawDate ? new Date(rawDate) : null;
        if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
          day = parsedDate.getDate();
        }
      }

      if (!Number.isFinite(day) || day < 1 || day > daysInMonth) return;

      const revenueKey = revenueKeys.find((key) => key in item);
      const revenueValue = revenueKey ? item[revenueKey] : 0;
      const parsedRevenue = toNumericValue(revenueValue);
      const current = Number(dayRevenueMap.get(day)) || 0;
      dayRevenueMap.set(day, current + parsedRevenue);
    });

    const values = categories.map((day) => Number(dayRevenueMap.get(Number(day))) || 0);
    return { categories, values };
  };

  const buildMonthlyRevenueSeries = (payload) => {
    const { categories, values } = parseRevenueApiData(payload, "monthly");
    const monthRevenueMap = new Map();

    categories.forEach((category, index) => {
      const monthKey = toMonthShort(category);
      if (!HEATMAP_MONTHS.includes(monthKey)) return;

      monthRevenueMap.set(monthKey, Number(values[index]) || 0);
    });

    return {
      categories: HEATMAP_MONTHS,
      values: HEATMAP_MONTHS.map((month) => monthRevenueMap.get(month) || 0),
    };
  };

  const buildHotelDailyRevenueHeatmapSeries = (payload, year, month) => {
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    const categories = Array.from({ length: daysInMonth }, (_, index) => String(index + 1));
    const rows = Array.isArray(payload)
      ? payload
      : payload?.daily ||
        payload?.dailyRevenue ||
        payload?.hotelDailyRevenue ||
        payload?.rows ||
        payload?.list ||
        payload?.items ||
        payload?.data ||
        [];

    const revenueKeys = [
      "Revenue",
      "revenue",
      "TotalRevenue",
      "totalRevenue",
      "DailyRevenue",
      "dailyRevenue",
      "Amount",
      "amount",
      "Value",
      "value",
      "Total",
      "total",
    ];
    const hotelLabelKeys = [
      "HotelName",
      "hotelName",
      "UnitName",
      "unitName",
      "PropertyName",
      "propertyName",
      "Hotel",
      "hotel",
      "Label",
      "label",
      "name",
    ];

    const resolveDay = (item) => {
      let day = Number(item?.Day ?? item?.day);

      if (!Number.isFinite(day) || day <= 0) {
        const rawDate =
          item?.Date ||
          item?.date ||
          item?.RevenueDate ||
          item?.revenueDate ||
          item?.BookingDate ||
          item?.bookingDate;
        const parsedDate = rawDate ? new Date(rawDate) : null;
        if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
          day = parsedDate.getDate();
        }
      }

      return Number.isFinite(day) && day >= 1 && day <= daysInMonth ? day : null;
    };

    const groupedSeries = new Map();

    rows.forEach((item, index) => {
      if (!item || typeof item !== "object") return;

      const labelKey = hotelLabelKeys.find((key) => key in item);
      const label = String(labelKey ? item[labelKey] : `Hotel ${index + 1}`);
      const day = resolveDay(item);
      if (!day) return;

      const revenueKey = revenueKeys.find((key) => key in item);
      const amount = revenueKey ? toNumericValue(item[revenueKey]) : 0;

      if (!groupedSeries.has(label)) {
        groupedSeries.set(label, new Map());
      }

      const dailyMap = groupedSeries.get(label);
      dailyMap.set(day, (Number(dailyMap.get(day)) || 0) + amount);
    });

    return {
      categories,
      series: Array.from(groupedSeries.entries())
        .map(([name, dailyMap]) => ({
          name,
          data: categories.map((day) => ({
            x: day,
            y: Number(dailyMap.get(Number(day))) || 0,
          })),
        }))
        .filter((item) => item.data.some((value) => value.y > 0)),
    };
  };

  const buildHotelDailyOccupancyHeatmapSeries = (payload, year, month, hotels = []) => {
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    const categories = Array.from({ length: daysInMonth }, (_, index) => String(index + 1));
    const rows = Array.isArray(payload)
      ? payload
      : payload?.daily ||
        payload?.dailyOccupancy ||
        payload?.hotelDailyOccupancy ||
        payload?.rows ||
        payload?.list ||
        payload?.items ||
        payload?.data ||
        [];

    const hotelLookup = new Map(
      (Array.isArray(hotels) ? hotels : []).map((hotel) => [
        Number(hotel?.PropertyID),
        hotel?.UnitName || hotel?.PropertyName || `Hotel ${hotel?.PropertyID}`,
      ]),
    );

    const groupedSeries = new Map();

    hotelLookup.forEach((hotelName) => {
      if (!groupedSeries.has(hotelName)) {
        groupedSeries.set(hotelName, new Map());
      }
    });

    rows.forEach((item) => {
      if (!item || typeof item !== "object") return;

      const hotelId = Number(item?.intHoteId ?? item?.hotelId ?? item?.HotelId);
      const rawDate = item?.OccupancyDate ?? item?.Date ?? item?.date;
      const parsedDate = rawDate ? new Date(rawDate) : null;
      const occupiedRooms = toNumericValue(item?.OccupiedRooms ?? item?.occupiedRooms);
      const responseHotelName =
        item?.HotelName ??
        item?.hotelName ??
        item?.UnitName ??
        item?.unitName ??
        item?.PropertyName ??
        item?.propertyName;

      if (!hotelId || !parsedDate || Number.isNaN(parsedDate.getTime())) return;

      const day = parsedDate.getDate();
      if (day < 1 || day > daysInMonth) return;

      const hotelName = responseHotelName || hotelLookup.get(hotelId) || `Hotel ${hotelId}`;
      if (!groupedSeries.has(hotelName)) {
        groupedSeries.set(hotelName, new Map());
      }

      const dailyMap = groupedSeries.get(hotelName);
      dailyMap.set(day, occupiedRooms);
    });

    return {
      categories,
      series: Array.from(groupedSeries.entries()).map(([name, dailyMap]) => ({
        name,
        data: categories.map((day) => ({
          x: day,
          y: Number(dailyMap.get(Number(day))) || 0,
        })),
      })),
    };
  };

  const getMonthlySnapshotValue = (payload, monthNumber, preferredKeys = []) => {
    if (payload == null) return 0;

    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      for (const key of preferredKeys) {
        if (key in payload) {
          const preferredValue = toNumericValue(payload[key]);
          if (preferredValue || preferredValue === 0) return preferredValue;
        }
      }
    }

    const targetMonth = HEATMAP_MONTHS[Number(monthNumber) - 1];
    const { categories, values } = buildMonthlyRevenueSeries(payload);
    const monthIndex = categories.findIndex((month) => month === targetMonth);

    if (monthIndex >= 0) {
      return Number(values[monthIndex]) || 0;
    }

    return getRevenueValue(payload);
  };

  const toDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const yearList = Array.from(
    { length: currentYear - 2016 + 1 },
    (_, i) => currentYear - i,
  );
  const revenueYearList = yearList.filter((year) => year >= MIN_REVENUE_YEAR);

  useEffect(() => {
    const hotelId = user?.user?.PropertyID;

    if (hotelId !== null && hotelId !== undefined) {
      const fetchSingleHotel = async () => {
        try {
          const res = await axios.get(
            `${REACT_APP_API_URL}api/dashboard/admin/hotel/${hotelId}`,{withCredentials:true}
          );

          const hotel = res.data.data?.[0];
          if (hotel) {
            setAvailableHotels([hotel]);
            setSelectedHotel(hotel.PropertyID);
            getChartData(hotel.PropertyID, selectedYear);
          }
        } catch (error) {
          console.error("Error fetching specific hotel:", error);
        }
      };

      fetchSingleHotel();
    } else {
      dispatch(fetchHotel());
    }
  }, [dispatch, user, selectedYear]);

  useEffect(() => {
    if (!user?.user?.PropertyID) {
      setAvailableHotels(allHotel);
    }
  }, [allHotel, user]);

  useEffect(() => {
    if (user?.user?.PropertyID) return;
    if (selectedHotel || !availableHotels.length) return;

    const defaultHotelId = availableHotels[0]?.PropertyID;
    if (!defaultHotelId) return;

    setSelectedHotel(defaultHotelId);
    getChartData(defaultHotelId, selectedYear);
  }, [availableHotels, selectedHotel, selectedYear, user]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!canViewDashboardStats) {
        setDashboardData(null);
        return;
      }

      try {
        const res = await axios.get(
          `${REACT_APP_API_URL}api/dashboard/admin/today-bookings`,
          {
            withCredentials: true,
            params: {
              HoteId: userHotelId || null,
            },
          },
        );

        if (res.data.success) {
          setDashboardData(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching today's dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, [canViewDashboardStats, userHotelId]);

  useEffect(() => {
    const fetchRoomOccupancySummary = async () => {
      if (!isAdminUser) return;

      setOccupancyLoading(true);
      setOccupancyError("");

      try {
        const res = await axios.get(
          `${REACT_APP_API_URL}api/dashboard/admin/kpi/room-occupancy-summary`,
          {
            withCredentials: true,
            params: {
              HoteId: user?.user?.PropertyID || null,
            },
          },
        );

        const { entries, summary } = parseOccupancyData(res?.data?.data);
        const hasValidData = entries.some((item) => item.value > 0);

        if (!entries.length || !hasValidData) {
          setOccupancySeries([]);
          setOccupancyOptions((prev) => ({ ...prev, labels: [] }));
          setOccupancySummary(summary);
          setOccupancyError("No occupancy data available.");
          return;
        }

        setOccupancySeries(entries.map((item) => item.value));
        setOccupancySummary(summary);
        setOccupancyOptions((prev) => ({
          ...prev,
          labels: entries.map((item) => item.label),
        }));
      } catch (error) {
        console.error("Error fetching room occupancy summary:", error);
        setOccupancySeries([]);
        setOccupancyOptions((prev) => ({ ...prev, labels: [] }));
        setOccupancyError("Failed to load room occupancy summary.");
      } finally {
        setOccupancyLoading(false);
      }
    };

    fetchRoomOccupancySummary();
  }, [isAdminUser, user?.user?.PropertyID]);

  useEffect(() => {
    const fetchHotelOccupancyHeatmap = async () => {
      if (!isAdminUser) return;

      setHeatmapLoading(true);
      setHeatmapError("");

      try {
        const monthStart = new Date(Number(selectedYear), Number(selectedHeatmapMonth) - 1, 1);
        const monthEnd = new Date(Number(selectedYear), Number(selectedHeatmapMonth), 0);
        const fromDate = toDateString(monthStart);
        const toDate = toDateString(monthEnd);
        const hotelsRes = await axios.get(`${REACT_APP_API_URL}api/room-tariff/get-hotels`,{withCredentials:true});
        const hotels = hotelsRes?.data?.data || [];

        if (!hotels.length) {
          setHeatmapSeries([]);
          setHeatmapError("No hotels found.");
          return;
        }

        const res = await axios.get(
          `${REACT_APP_API_URL}api/dashboard/admin/kpi/hotel-daily-occupancy`,
          {
            withCredentials: true,
            params: {
              fromDate,
              toDate,
            },
          },
        );
        const payload = res?.data?.data || [];
        const { categories, series } = buildHotelDailyOccupancyHeatmapSeries(
          payload,
          selectedYear,
          selectedHeatmapMonth,
          hotels,
        );

        const maxOccupancy = Math.max(
          0,
          ...series.flatMap((hotel) => hotel.data.map((entry) => Number(entry.y) || 0)),
        );

        if (!categories.length || !series.length) {
          setHeatmapSeries([]);
          setHeatmapOptions((prev) => ({
            ...prev,
            xaxis: {
              ...prev.xaxis,
              categories: [],
            },
          }));
          setHeatmapError("No occupancy data available for the selected month.");
          return;
        }

        setHeatmapSeries(series);
        setHeatmapOptions((prev) => ({
          ...prev,
          xaxis: {
            ...prev.xaxis,
            categories,
            title: {
              text: "Day of Month",
            },
          },
          tooltip: {
            y: {
              formatter: (val) => `${val} Rooms`,
            },
          },
          plotOptions: {
            ...prev.plotOptions,
            heatmap: {
              ...prev.plotOptions?.heatmap,
              shadeIntensity: 0.5,
              radius: 2,
              colorScale: {
                ranges: [
                  { from: 0, to: 0, color: "#E9ECEF", name: "0 Rooms" },
                  {
                    from: 0.01,
                    to: maxOccupancy > 0 ? maxOccupancy * 0.25 : 1,
                    color: "#1f77b4",
                    name: "Low",
                  },
                  {
                    from: maxOccupancy > 0 ? maxOccupancy * 0.25 + 0.01 : 1.01,
                    to: maxOccupancy > 0 ? maxOccupancy * 0.5 : 2,
                    color: "#fdc70c",
                    name: "Moderate",
                  },
                  {
                    from: maxOccupancy > 0 ? maxOccupancy * 0.5 + 0.01 : 2.01,
                    to: maxOccupancy > 0 ? maxOccupancy * 0.75 : 3,
                    color: "#f3903f",
                    name: "High",
                  },
                  {
                    from: maxOccupancy > 0 ? maxOccupancy * 0.75 + 0.01 : 3.01,
                    to: Math.max(maxOccupancy, 4),
                    color: "#e93e3a",
                    name: "Peak",
                  },
                ],
              },
            },
          },
        }));
      } catch (error) {
        console.error("Error fetching occupancy heatmap data:", error);
        setHeatmapSeries([]);
        setHeatmapError("Failed to load occupancy heatmap.");
      } finally {
        setHeatmapLoading(false);
      }
    };

    fetchHotelOccupancyHeatmap();
  }, [isAdminUser, selectedYear, selectedHeatmapMonth]);

  useEffect(() => {
    const fetchMonthlySnapshot = async () => {
      if (!isAdminUser) return;

      setMonthlySnapshotLoading(true);
      setMonthlySnapshotError("");

      try {
        const currentMonth = new Date().getMonth() + 1;
        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const previousMonthYear =
          currentMonth === 1 ? Number(selectedRevenueYear) - 1 : Number(selectedRevenueYear);

        const [currentMonthRes, lastMonthRes] = await Promise.all([
          axios.get(`${REACT_APP_API_URL}api/dashboard/admin/kpi/revenue`, {
            withCredentials: true,
            params: {
              type: "monthly",
              month: currentMonth,
              year: Number(selectedRevenueYear),
            },
          }),
          axios.get(`${REACT_APP_API_URL}api/dashboard/admin/kpi/revenue`, {
            withCredentials: true,
            params: {
              type: "monthly",
              month: previousMonth,
              year: previousMonthYear,
            },
          }),
        ]);

        const currentPayload = currentMonthRes?.data?.data;
        const lastPayload = lastMonthRes?.data?.data;
        const currentValue = getMonthlySnapshotValue(currentPayload, currentMonth, [
          "currentMonthRevenue",
          "Revenue",
          "revenue",
          "TotalRevenue",
          "totalRevenue",
          "MonthlyRevenue",
          "monthlyRevenue",
          "TotalAmount",
          "totalAmount",
          "Amount",
          "amount",
        ]);
        const lastValue = getMonthlySnapshotValue(lastPayload, previousMonth, [
          "lastMonthRevenue",
          "currentMonthRevenue",
          "Revenue",
          "revenue",
          "TotalRevenue",
          "totalRevenue",
          "MonthlyRevenue",
          "monthlyRevenue",
          "TotalAmount",
          "totalAmount",
          "Amount",
          "amount",
        ]);

        setMonthlySnapshot({
          currentMonthRevenue: currentValue,
          lastMonthRevenue: lastValue,
        });
      } catch (error) {
        console.error("Error fetching monthly revenue snapshot:", error);
        setMonthlySnapshot({
          currentMonthRevenue: 0,
          lastMonthRevenue: 0,
        });
        setMonthlySnapshotError("Failed to load monthly revenue snapshot.");
      } finally {
        setMonthlySnapshotLoading(false);
      }
    };

    fetchMonthlySnapshot();
  }, [isAdminUser, user?.user?.PropertyID, selectedRevenueYear]);

  useEffect(() => {
    const fetchRevenueByType = async () => {
      if (!isAdminUser) return;

      setRevenueChartLoading(true);
      setRevenueChartError("");

      try {
        const revenueParams = {
          type: selectedRevenueType,
        };

        if (selectedRevenueType === "monthly") {
          revenueParams.year = Number(selectedRevenueYear);
        } else if (selectedRevenueType === "yearly") {
          revenueParams.year = Number(selectedRevenueYear);
        } else if (selectedRevenueType === "daily") {
          revenueParams.month = Number(selectedRevenueMonth);
          revenueParams.year = Number(selectedRevenueYear);
        }

        const res = await axios.get(`${REACT_APP_API_URL}api/dashboard/admin/kpi/revenue`, {
          withCredentials: true,
          params: revenueParams,
        });

        const payload = res?.data?.data || {};
        const { categories, values } =
          selectedRevenueType === "daily"
            ? buildDailyRevenueSeries(payload, selectedRevenueYear, selectedRevenueMonth)
            : selectedRevenueType === "monthly"
              ? buildMonthlyRevenueSeries(payload)
              : parseRevenueApiData(payload, selectedRevenueType);

        if (!categories.length) {
          setRevenueSeries([]);
          setRevenueOptions((prev) => ({
            ...prev,
            xaxis: { ...prev.xaxis, categories: [] },
          }));
          setRevenueChartError("No revenue data available for the selected filter.");
          return;
        }

        const selectedLabel =
          REVENUE_FILTER_OPTIONS.find((item) => item.value === selectedRevenueType)?.label ||
          "Revenue";
        const xAxisTitleMap = {
          daily: "Day of Month",
          monthly: "Month",
          yearly: "Year",
        };
        const isYearlyRevenue = selectedRevenueType === "yearly";
        const chartType = "area";

        setRevenueChartType(chartType);
        setRevenueSeries([
          {
            name: `${selectedLabel} Revenue`,
            data: values,
          },
        ]);
        setRevenueOptions((prev) => ({
          ...prev,
          chart: {
            ...prev.chart,
            type: chartType,
          },
          stroke: {
            ...prev.stroke,
            curve: isYearlyRevenue ? "straight" : "smooth",
            width: isYearlyRevenue ? 3 : 2,
          },
          fill: isYearlyRevenue
            ? {
                type: "gradient",
                gradient: {
                  shadeIntensity: 1,
                  opacityFrom: 0.32,
                  opacityTo: 0.04,
                  stops: [0, 100],
                },
              }
            : prev.fill,
          markers: {
            ...prev.markers,
            size: isYearlyRevenue ? 4 : 0,
            hover: { size: isYearlyRevenue ? 6 : 5 },
          },
          dataLabels: {
            enabled: isYearlyRevenue,
            offsetY: -8,
            style: {
              fontSize: "12px",
              fontWeight: "600",
              colors: ["#111827"],
            },
            formatter: (value) => {
              if (!Number.isFinite(Number(value))) return "";
              return new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
                notation: "compact",
                maximumFractionDigits: 1,
              }).format(Number(value));
            },
          },
          xaxis: {
            ...prev.xaxis,
            categories,
            title: {
              text: xAxisTitleMap[selectedRevenueType] || "Timeline",
            },
          },
        }));
      } catch (error) {
        console.error("Error fetching revenue KPI by type:", error);
        setRevenueSeries([]);
        setRevenueChartError("Failed to load revenue graph.");
      } finally {
        setRevenueChartLoading(false);
      }
    };

    fetchRevenueByType();
  }, [
    isAdminUser,
    user?.user?.PropertyID,
    selectedRevenueYear,
    selectedRevenueType,
    selectedRevenueMonth,
  ]);

  useEffect(() => {
    const fetchHotelDailyRevenue = async () => {
      if (!isAdminUser) return;

      setHotelRevenueLoading(true);
      setHotelRevenueError("");

      try {
        const res = await axios.get(
          `${REACT_APP_API_URL}api/dashboard/admin/kpi/hotel-daily-revenue`,
          {
            withCredentials: true,
            params: {
              year: Number(selectedHotelRevenueYear),
              month: Number(selectedHotelRevenueMonth),
            },
          },
        );

        const payload = res?.data?.data || [];
        const { categories, series } = buildHotelDailyRevenueHeatmapSeries(
          payload,
          selectedHotelRevenueYear,
          selectedHotelRevenueMonth,
        );
        const maxRevenue = Math.max(
          0,
          ...series.flatMap((hotel) => hotel.data.map((entry) => Number(entry.y) || 0)),
        );

        if (!categories.length || !series.length) {
          setHotelRevenueSeries([]);
          setHotelRevenueOptions((prev) => ({
            ...prev,
            xaxis: {
              ...prev.xaxis,
              categories: [],
            },
          }));
          setHotelRevenueError("No hotel daily revenue data available for the selected month.");
          return;
        }

        setHotelRevenueSeries(series);
        setHotelRevenueOptions((prev) => ({
          ...prev,
          xaxis: {
            ...prev.xaxis,
            categories,
            title: {
              text: "Day of Month",
            },
          },
          plotOptions: {
            ...prev.plotOptions,
            heatmap: {
              ...prev.plotOptions?.heatmap,
              colorScale: {
                ranges: [
                  { from: 0, to: 0, color: "#E9ECEF", name: "No Revenue" },
                  {
                    from: 0.01,
                    to: maxRevenue > 0 ? maxRevenue * 0.25 : 1,
                    color: "#dbeafe",
                    name: "Low",
                  },
                  {
                    from: maxRevenue > 0 ? maxRevenue * 0.25 + 0.01 : 1.01,
                    to: maxRevenue > 0 ? maxRevenue * 0.5 : 2,
                    color: "#93c5fd",
                    name: "Moderate",
                  },
                  {
                    from: maxRevenue > 0 ? maxRevenue * 0.5 + 0.01 : 2.01,
                    to: maxRevenue > 0 ? maxRevenue * 0.75 : 3,
                    color: "#3b82f6",
                    name: "High",
                  },
                  {
                    from: maxRevenue > 0 ? maxRevenue * 0.75 + 0.01 : 3.01,
                    to: Math.max(maxRevenue, 4),
                    color: "#1d4ed8",
                    name: "Peak",
                  },
                ],
              },
            },
          },
        }));
      } catch (error) {
        console.error("Error fetching hotel daily revenue graph:", error);
        setHotelRevenueSeries([]);
        setHotelRevenueError("Failed to load hotel daily revenue graph.");
      } finally {
        setHotelRevenueLoading(false);
      }
    };

    fetchHotelDailyRevenue();
  }, [isAdminUser, selectedHotelRevenueYear, selectedHotelRevenueMonth]);

  useEffect(() => {
    const fetchCancellationTrend = async () => {
      if (!isAdminUser) return;

      setCancellationLoading(true);
      setCancellationError("");

      try {
        const now = new Date();
        const fiscalStartMonth = 4;
        let fromYear = now.getFullYear();
        if (now.getMonth() < fiscalStartMonth) {
          fromYear -= 1;
        }
        const fromDate = `${fromYear}-05-01`;
        const toDate = `${fromYear + 1}-03-31`;

        const res = await axios.get(
          `${REACT_APP_API_URL}api/dashboard/admin/kpi/cancellation-trend`,
          {
            withCredentials: true,
            params: { fromDate, toDate },
          },
        );

        const payload = res?.data?.data || res?.data || [];
        const rows = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.list || payload?.rows || [];

        if (!rows.length) {
          setCancellationData([]);
          setCancellationError("No cancellation data available for the selected period.");
          return;
        }

        setCancellationData(rows);
      } catch (error) {
        console.error("Error fetching cancellation trend:", error);
        setCancellationData([]);
        setCancellationError("Failed to load cancellation trend data.");
      } finally {
        setCancellationLoading(false);
      }
    };

    fetchCancellationTrend();
  }, [isAdminUser]);

  const fetchYearlyChartData = async (hotelId, year) => {
    const res = await axios.post(`${REACT_APP_API_URL}api/dashboard/admin/hotelchartdata`, {
      firstCondition: Number(hotelId) || 0,
      secondCondition: Number(year),
    }, {withCredentials: true});

    return res.data.data || [];
  };

  const getChartData = async (hotelId, year) => {
    try {
      const selectedYearNumber = Number(year);
      const lastYearNumber = selectedYearNumber - 1;

      const [currentYearResponse, lastYearResponse] = await Promise.all([
        fetchYearlyChartData(hotelId, selectedYearNumber),
        fetchYearlyChartData(hotelId, lastYearNumber),
      ]);

      const currentYearData = mapMonthlyData(currentYearResponse);
      const lastYearData = mapMonthlyData(lastYearResponse);

      setChartSeries([
        {
          name: `Current Year (${selectedYearNumber})`,
          data: currentYearData,
        },
        {
          name: `Last Year (${lastYearNumber})`,
          data: lastYearData,
        },
      ]);

      setComparisonYears({
        current: selectedYearNumber,
        previous: lastYearNumber,
      });

      setChartOptions((prevOptions) => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: MONTH_ORDER,
        },
      }));
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const handleHotelChange = (e) => {
    const hotelId = e.target.value;
    setSelectedHotel(hotelId);
    getChartData(hotelId, selectedYear);
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    const activeHotelId = selectedHotel || availableHotels[0]?.PropertyID || "";
    setSelectedYear(year);

    if (activeHotelId) {
      if (!selectedHotel) {
        setSelectedHotel(activeHotelId);
      }
      getChartData(activeHotelId, year);
    }
  };

  const handleChartTypeChange = (e) => {
    setSelectedChartType(e.target.value);
  };

  const handleRevenueTypeChange = (e) => {
    setSelectedRevenueType(e.target.value);
  };

  const handleRevenueMonthChange = (e) => {
    setSelectedRevenueMonth(e.target.value);
  };

  const handleRevenueYearChange = (e) => {
    setSelectedRevenueYear(Math.max(Number(e.target.value), MIN_REVENUE_YEAR));
  };

  const handleHeatmapMonthChange = (e) => {
    setSelectedHeatmapMonth(e.target.value);
  };

  const handleHotelRevenueYearChange = (e) => {
    setSelectedHotelRevenueYear(Math.max(Number(e.target.value), MIN_REVENUE_YEAR));
  };

  const handleHotelRevenueMonthChange = (e) => {
    setSelectedHotelRevenueMonth(e.target.value);
  };

  const currentMonthRevenue = Number(monthlySnapshot.currentMonthRevenue) || 0;
  const lastMonthRevenue = Number(monthlySnapshot.lastMonthRevenue) || 0;
  const revenueDelta = currentMonthRevenue - lastMonthRevenue;
  const revenueDeltaPercent =
    lastMonthRevenue > 0 ? (revenueDelta / lastMonthRevenue) * 100 : 0;

  const typeLabel =
    REVENUE_FILTER_OPTIONS.find((item) => item.value === selectedRevenueType)?.label ||
    "Revenue";
  const todayActivityOptions = {
    chart: { type: "donut" },
    labels: ["Check-Ins", "Cancelled"],
    colors: ["#0d6efd", "#dc3545"],
    dataLabels: { enabled: true },
    legend: { position: "bottom" },
  };

  const todayActivitySeries = dashboardData
    ? [
        Number(dashboardData.TotalBookings) || 0,
        Number(dashboardData.TotalCancelled) || 0,
      ]
    : [];

  const momRevenueOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `\u20B9${val.toLocaleString("en-IN")}`,
    },
    xaxis: { categories: ["Last Month", "Current Month"] },
    colors: ["#6c757d", "#198754"],
    legend: { show: false },
  };

  const momRevenueSeries = [
    {
      name: "Revenue",
      data: [lastMonthRevenue, currentMonthRevenue],
    },
  ];

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      {canViewDashboardStats && (
        <DashboardStatsCards
          dashboardData={dashboardData}
          currentMonthRevenue={currentMonthRevenue}
          lastMonthRevenue={lastMonthRevenue}
          visibleCardKeys={
            isManagerUser ? ["todayCheckIn", "todayBooking"] : undefined
          }
        />
      )}

      {isAdminUser && (
        <AdminRevenueKpiSection
          monthlySnapshotLoading={monthlySnapshotLoading}
          monthlySnapshotError={monthlySnapshotError}
          selectedRevenueType={selectedRevenueType}
          onRevenueTypeChange={handleRevenueTypeChange}
          currentMonthRevenue={currentMonthRevenue}
          lastMonthRevenue={lastMonthRevenue}
          revenueDelta={revenueDelta}
          revenueDeltaPercent={revenueDeltaPercent}
          revenueChartLoading={revenueChartLoading}
          revenueChartError={revenueChartError}
          revenueSeries={revenueSeries}
          revenueOptions={revenueOptions}
          revenueChartType={revenueChartType}
          selectedRevenueYear={selectedRevenueYear}
          yearList={revenueYearList}
          onRevenueYearChange={handleRevenueYearChange}
          selectedRevenueMonth={selectedRevenueMonth}
          onRevenueMonthChange={handleRevenueMonthChange}
          revenueTitle={`${typeLabel} Revenue Graph (${
            selectedRevenueType === "daily"
              ? HEATMAP_MONTHS[selectedRevenueMonth - 1] + " "
              : ""
          }${selectedRevenueYear})`}
        />
      )}

     
      {isAdminUser && (
        <AdminHotelDailyRevenueSection
          hotelRevenueLoading={hotelRevenueLoading}
          hotelRevenueError={hotelRevenueError}
          hotelRevenueSeries={hotelRevenueSeries}
          hotelRevenueOptions={hotelRevenueOptions}
          selectedHotelRevenueYear={selectedHotelRevenueYear}
          selectedHotelRevenueMonth={selectedHotelRevenueMonth}
          yearList={revenueYearList}
          onHotelRevenueYearChange={handleHotelRevenueYearChange}
          onHotelRevenueMonthChange={handleHotelRevenueMonthChange}
          hotelRevenueTitle={`Hotel Daily Revenue (${HEATMAP_MONTHS[selectedHotelRevenueMonth - 1]} ${selectedHotelRevenueYear})`}
        />
      )}

      {isAdminUser && (
        <AdminOccupancySection
          selectedYear={selectedYear}
          selectedMonth={selectedHeatmapMonth}
          yearList={yearList}
          onHeatmapYearChange={handleYearChange}
          onHeatmapMonthChange={handleHeatmapMonthChange}
          // occupancyLoading={occupancyLoading}
          // occupancyError={occupancyError}
          // occupancySeries={occupancySeries}
          // occupancyOptions={occupancyOptions}
          // occupancySummary={occupancySummary}
          heatmapLoading={heatmapLoading}
          heatmapError={heatmapError}
          heatmapSeries={heatmapSeries}
          heatmapOptions={heatmapOptions}
        />
      )}

      {isAdminUser && (
        <ActivityRevenueSection
          dashboardData={dashboardData}
          isAdminUser={isAdminUser}
          todayActivityOptions={todayActivityOptions}
          todayActivitySeries={todayActivitySeries}
          momRevenueOptions={momRevenueOptions}
          momRevenueSeries={momRevenueSeries}
        />
      )}

      <BookingTrendsSection
        comparisonYears={comparisonYears}
        selectedHotel={selectedHotel}
        availableHotels={availableHotels}
        selectedYear={selectedYear}
        yearList={yearList}
        selectedChartType={selectedChartType}
        handleHotelChange={handleHotelChange}
        handleYearChange={handleYearChange}
        handleChartTypeChange={handleChartTypeChange}
        chartOptions={chartOptions}
        chartSeries={chartSeries}
      />
      {isAdminUser && (
        <AdminTopRevenueHotelsSection yearList={revenueYearList} />
      )}

      {isAdminUser && (
        <CancellationTrendSection
          cancellationData={cancellationData}
          cancellationLoading={cancellationLoading}
          cancellationError={cancellationError}
        />
      )}

    </div>
  );
};

export default Index;
